import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import 'i18n/config';
import 'simplebar-react/dist/simplebar.min.css';
import 'styles/index.css';
import 'styles/custom.css';
import 'styles/map-animations.css';

import "shepherd.js/dist/css/shepherd.css";
import "onboarding/styles/tour.css";

import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';

import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PrimeReactProvider } from 'primereact/api';
import 'primereact/resources/primereact.css';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import initFetchInterceptor from 'utils/fetchInterceptor';
import { toast } from 'sonner';

// Import theme CSS based on environment variable
const clientTheme = import.meta.env.VITE_CLIENT_THEME || 'henddu';

const getErrorMessage = (error) =>
  error?.message || error?.response?.message || 'An unexpected error occurred.';

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.meta?.suppressGlobalError) return;
      toast.error(getErrorMessage(error));
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.meta?.suppressGlobalError) return;
      toast.error(getErrorMessage(error));
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

// Initialize fetch interceptor early
initFetchInterceptor();

// Function to render the app
const renderApp = () => {
  ReactDOM.createRoot(document.getElementById('root')).render(
    <PrimeReactProvider>
      <QueryClientProvider client={queryClient}>
        <React.StrictMode>
            <App />
        </React.StrictMode>
        <ReactQueryDevtools />
      </QueryClientProvider>
    </PrimeReactProvider>
  );
};

// Load theme CSS before rendering to ensure CSS variables are available
import(`./styles/themes/${clientTheme}.css`)
  .then(() => {
    renderApp();
  })
  .catch(() => {
    // Fallback to henddu theme if dynamic import fails
    import('./styles/themes/henddu.css').then(() => {
      renderApp();
    });
  });
